// Invoice detail — the client-facing document plus a money/action rail on
// desktop, a stacked card layout with a footer action bar on mobile.
// Actions follow the status: Unpaid / Upcoming → Open appointment (the
// existing appointment details — checkout, no-show and cancel all happen
// there; money is only ever taken at its checkout) · Paid → receipt ·
// Void → record kept (View appointment if the appointment still exists).
import { useState } from 'react'
import { Box, ButtonBase } from '@mui/material'
import { T, card, overline, StatusPill, PawIcon, InvIcon } from './invoicesUi'

// The store logo on the document header, falling back to the initials badge
// when the merchant has no logo (or the image fails to load).
function BrandLogo({ url, name, size = 42 }) {
	const [failed, setFailed] = useState(false)
	if (!url || failed) {
		return (
			<Box sx={{ width: size, height: size, flex: 'none', borderRadius: '50%', bgcolor: T.primary, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 800, letterSpacing: '0.02em' }}>
				{(name || '?').split(' ').map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()}
			</Box>
		)
	}
	return (
		<Box component="img" src={url} alt="" onError={() => setFailed(true)} sx={{
			width: size, height: size, flex: 'none', borderRadius: '50%', objectFit: 'cover',
			bgcolor: T.primarySoft, display: 'block',
		}} />
	)
}

// The pet's avatar photo, falling back to the paw mark when the pet has no
// photo (or the image fails to load).
function PetHeadAvatar({ url, size = 32 }) {
	const [failed, setFailed] = useState(false)
	if (!url || failed) {
		return (
			<Box sx={{ width: size, height: size, flex: 'none', borderRadius: '50%', bgcolor: T.primarySoft, color: T.primaryDeep, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
				<PawIcon size={Math.round(size / 2)} />
			</Box>
		)
	}
	return (
		<Box component="img" src={url} alt="" onError={() => setFailed(true)} sx={{
			width: size, height: size, flex: 'none', borderRadius: '50%', objectFit: 'cover',
			bgcolor: T.primarySoft, display: 'block',
		}} />
	)
}

function VoidBanner({ inv }) {
	return (
		<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '9px', p: '11px 13px', borderRadius: '12px', bgcolor: T.voidBg, color: T.voidText, fontSize: '12.5px', fontWeight: 600, lineHeight: 1.5, fontFamily: T.font }}>
			<Box sx={{ mt: '2px', display: 'flex' }}><InvIcon name="voidx" size={15} /></Box>
			<span>
				<Box component="b" sx={{ fontWeight: 750, color: T.text }}>Voided{inv.voidedAtFmt ? ` ${inv.voidedAtFmt}` : ''}</Box>
				{' — '}{inv.voidWhat}. Nothing is owed; the record is kept and can't be edited or deleted.
			</span>
		</Box>
	)
}

function JustPaidBanner({ inv }) {
	const pay = inv.payments[0]
	return (
		<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '10px', p: '12px 13px', borderRadius: '12px', bgcolor: T.okBg, border: '1px solid #C9E8D4', color: T.okText, fontSize: '12.5px', fontWeight: 600, lineHeight: 1.5, fontFamily: T.font }}>
			<Box sx={{ width: 26, height: 26, flex: 'none', borderRadius: '50%', bgcolor: T.okIcon, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
				<InvIcon name="tick" size={14} sw={2.1} />
			</Box>
			<Box sx={{ flex: 1, minWidth: 0 }}>
				<Box component="b" sx={{ fontWeight: 750 }}>Checked out — you're back on {inv.number}.</Box>
				{' '}{pay ? `${inv.money(pay.amount)} paid by ${pay.methodLabel.toLowerCase()} at checkout, just now.` : ''} It's now {inv.clientFirst}'s receipt.
			</Box>
		</Box>
	)
}

function ItemsByPet({ inv, petAvatars }) {
	return (
		<Box sx={{ display: 'flex', flexDirection: 'column' }}>
			{inv.pets.map((pet, i) => (
				<Box key={i} sx={{ display: 'flex', flexDirection: 'column' }}>
					{i > 0 && <Box sx={{ height: '1px', bgcolor: T.divider, m: '12px 0 13px' }} />}
					{!pet.generic && (
						<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: '4px' }}>
							<PetHeadAvatar url={pet.petId ? petAvatars?.[pet.petId] : null} />
							<Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: '8px' }}>
								<Box component="strong" sx={{ fontSize: '13.5px', fontWeight: 750, color: T.text }}>{pet.name}</Box>
								<Box sx={{ fontSize: '12px', fontWeight: 600, color: T.breed }}>{pet.breed}</Box>
							</Box>
							<Box sx={{ fontSize: '12.5px', fontWeight: 750, color: T.soft, fontVariantNumeric: 'tabular-nums' }}>{inv.money(pet.subtotal)}</Box>
						</Box>
					)}
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px', p: `3px 0 3px ${pet.generic ? 0 : 42}px`, minWidth: 0 }}>
						{pet.service && (
							<Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '12px', minWidth: 0 }}>
								<Box component="span" sx={{ flex: 1, minWidth: 0, fontSize: '13px', fontWeight: 700, letterSpacing: '-0.005em', color: T.text, textWrap: 'pretty' }}>{pet.service.name}</Box>
								<Box sx={{ fontSize: '13px', fontWeight: 650, color: T.text, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{inv.money(pet.service.amount)}</Box>
							</Box>
						)}
						{pet.addons.map((a, j) => (
							<Box key={j} sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '12px', minWidth: 0 }}>
								<Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'flex-start', gap: '6px', fontSize: '12.5px', fontWeight: 600, color: T.soft, lineHeight: 1.35 }}>
									<Box component="span" aria-hidden="true" sx={{ flex: 'none', color: T.faint, fontWeight: 700 }}>+</Box>
									<Box component="span" sx={{ textWrap: 'pretty' }}>{a.name}</Box>
								</Box>
								<Box sx={{ fontSize: '12.5px', fontWeight: 650, color: T.soft, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{inv.money(a.amount)}</Box>
							</Box>
						))}
					</Box>
				</Box>
			))}
		</Box>
	)
}

function TotalsStack({ inv }) {
	const row = (label, value, opts = {}) => (
		<Box key={label} sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '12px', py: '4px', fontSize: '13px', fontWeight: 600, color: opts.color || T.soft }}>
			<span>{label}</span>
			<Box component="span" sx={{ fontWeight: opts.valueWeight || 650, color: opts.valueColor || T.text, fontVariantNumeric: 'tabular-nums' }}>{value}</Box>
		</Box>
	)
	const banner = inv.isUnpaid
		? { bg: T.primarySoft, color: T.primaryDeep, label: 'Balance due', value: inv.balanceFmt, big: true }
		: inv.isPaid
		? { bg: T.okBg, color: T.okText, label: 'Balance due', value: inv.money(0), tick: true }
		: inv.isUpcoming
		? { bg: T.surface, color: T.soft, label: 'Due at checkout', value: inv.totalFmt, valueColor: T.text }
		: { bg: T.voidBg, color: T.muted, label: 'Balance due', value: inv.money(0) }
	return (
		<Box sx={{ mt: '12px', pt: '11px', borderTop: `1px solid ${T.divider}`, display: 'flex', flexDirection: 'column', fontFamily: T.font }}>
			{row('Subtotal', inv.subtotalFmt)}
			{inv.discount > 0 && row(inv.discountLabel, inv.discountFmt)}
			{row(inv.taxLabel, inv.taxFmt)}
			{inv.tips > 0 && row('Tips', inv.tipsFmt)}
			<Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '12px', mt: '4px', p: '8px 0 4px', borderTop: `1px solid ${T.divider}`, fontSize: '13.5px', fontWeight: 750, color: T.text }}>
				<span>Total</span>
				<Box component="span" sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{inv.totalFmt}</Box>
			</Box>
			{inv.paid > 0 && row('Paid', `−${inv.paidFmt}`, { color: T.okText, valueColor: T.okText, valueWeight: 700 })}
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', mt: '8px', p: '11px 13px', borderRadius: '11px', bgcolor: banner.bg, color: banner.color, fontSize: '13.5px', fontWeight: 750 }}>
				<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '7px' }}>
					{banner.tick && <InvIcon name="tickCircle" size={15} sw={1.8} />}
					{banner.label}
				</Box>
				<Box component="span" sx={{ fontSize: banner.big ? '18px' : '15px', fontWeight: 800, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em', color: banner.valueColor || 'inherit' }}>{banner.value}</Box>
			</Box>
		</Box>
	)
}

function PaymentsCard({ inv, dense }) {
	return (
		<Box sx={{ ...card }}>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', p: '13px 15px 11px', borderBottom: '1px solid #EFEBF7', fontFamily: T.font }}>
				<Box sx={{ flex: 1, fontSize: '14px', fontWeight: 750, letterSpacing: '-0.01em', color: T.text }}>Payments</Box>
				<Box sx={{ fontSize: '11.5px', fontWeight: 650, color: T.muted }}>
					{inv.payments.length === 0 ? 'None yet' : `${inv.payments.length} payment${inv.payments.length === 1 ? '' : 's'}`}
				</Box>
			</Box>
			<Box sx={{ p: '12px 15px 13px', display: 'flex', flexDirection: 'column', gap: '10px', fontFamily: T.font }}>
				{inv.payments.map((p, i) => (
					<Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
						<Box sx={{ width: dense ? 30 : 32, height: dense ? 30 : 32, flex: 'none', borderRadius: dense ? '9px' : '10px', bgcolor: T.okBg, color: T.okIcon, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
							<InvIcon name="tick" size={14} sw={1.9} />
						</Box>
						<Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '1px' }}>
							<Box component="strong" sx={{ fontSize: '13px', fontWeight: 700, color: T.text }}>{p.methodLabel} · {p.dateFmt}</Box>
							<Box sx={{ fontSize: '11.5px', fontWeight: 600, color: T.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
								{p.refunded ? 'Refunded' : `At checkout${p.note ? ` · ${p.note}` : ''}`}
							</Box>
						</Box>
						<Box sx={{ fontSize: '13px', fontWeight: 750, color: T.okText, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{inv.money(p.amount)}</Box>
					</Box>
				))}
				{inv.payments.length === 0 && (
					<Box sx={{ fontSize: '12.5px', fontWeight: 600, color: T.muted, lineHeight: 1.5 }}>
						No payments yet. Payment is always taken at the appointment's checkout — <Box component="b" sx={{ fontWeight: 750, color: T.text }}>Open appointment</Box> when {inv.clientFirst} pays you.
					</Box>
				)}
			</Box>
		</Box>
	)
}

function HeroCopy(inv) {
	const pay = inv.payments[0]
	const label = inv.isUnpaid ? 'Balance due' : inv.isPaid ? 'Paid in full' : inv.isUpcoming ? 'Due at checkout' : 'Void'
	const amount = inv.isUnpaid ? inv.balanceFmt : inv.totalFmt
	const caption = inv.isUnpaid
		? `Groomed ${inv.dateFmt} · ${inv.rel.toLowerCase()}`
		: inv.isPaid
		? `${pay ? `Paid by ${pay.methodLabel.toLowerCase()} at checkout · ${pay.dateFmt}` : 'Paid'} · this is ${inv.clientFirst}'s receipt`
		: inv.isUpcoming
		? `${inv.dateFmt} · ${inv.time} · ${inv.rel.toLowerCase()}`
		: `Voided${inv.voidedAtFmt ? ` ${inv.voidedAtFmt}` : ''} · nothing is owed`
	return { label, amount, caption }
}

// Unpaid & Upcoming: the primary action opens the appointment itself (details,
// checkout, no-show, cancel) rather than jumping straight into checkout.
function OpenAppointmentButton({ inv, onOpenAppointment, height = 40 }) {
	return (
		<ButtonBase
			title="Opens the appointment — checkout, no-show and cancel all happen there"
			onClick={() => onOpenAppointment(inv)}
			sx={{
				width: '100%', height, gap: '8px', border: 'none', borderRadius: height >= 48 ? '12px' : '10px',
				bgcolor: T.primary, color: '#fff', fontSize: height >= 48 ? '14.5px' : '13.5px', fontWeight: 700,
				fontFamily: T.font, boxShadow: T.btnShadow, '&:hover': { bgcolor: T.primaryDeep },
			}}
		>
			<InvIcon name="calendar" size={height >= 48 ? 16 : 15} sw={1.7} />Open appointment
		</ButtonBase>
	)
}

/* ---------- the screen ---------- */

function DownloadButton({ inv, onDownload, downloading, height = 38, label, full }) {
	return (
		<ButtonBase
			disabled={downloading}
			onClick={() => onDownload(inv)}
			sx={{
				height, px: '13px', gap: '7px', ...(full ? { width: '100%' } : {}),
				border: `1.5px solid ${T.border}`, borderRadius: height >= 48 ? '12px' : '10px',
				bgcolor: T.field, color: T.text, fontSize: '13px', fontWeight: 650, fontFamily: T.font,
				whiteSpace: 'nowrap', opacity: downloading ? 0.6 : 1,
				'&:hover': { borderColor: T.primary, color: T.primaryDeep },
			}}
		>
			<InvIcon name="download" size={14} />
			{downloading ? 'Preparing PDF…' : (label || 'Download PDF')}
		</ButtonBase>
	)
}

export default function InvoiceDetail({ inv, merchant, isMobile, justPaid, onBack, backLabel = 'Back to invoices', onOpenAppointment, onDownload, downloading, petAvatars, logoUrl }) {
	const hero = HeroCopy(inv)
	const showPayments = inv.isUnpaid || inv.isPaid
	// A deleted appointment leaves nothing to open (appointment_id goes NULL).
	const canOpenAppointment = Boolean(onOpenAppointment && inv.appointmentId)

	if (isMobile) {
		return (
			<Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%', fontFamily: T.font }}>
				{/* header */}
				<Box sx={{ flex: 'none', display: 'flex', alignItems: 'center', gap: '10px', p: '10px 16px 11px 16px', bgcolor: '#FDFCFF', borderBottom: `1px solid ${T.border}` }}>
					<ButtonBase aria-label={backLabel} title={backLabel} onClick={onBack} sx={{ width: 40, height: 40, flex: 'none', ml: '-8px', borderRadius: '11px', color: T.text, '&:active': { bgcolor: T.surface } }}>
						<InvIcon name="back" size={18} sw={1.8} />
					</ButtonBase>
					<Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', fontSize: '19px', fontWeight: 800, letterSpacing: '-0.015em', fontVariantNumeric: 'tabular-nums', color: T.text }}>
							<span>{inv.number}</span>
							<StatusPill status={inv.status} />
						</Box>
						<Box sx={{ fontSize: '12px', fontWeight: 600, color: T.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
							{inv.clientName}{inv.petLine ? ` · ${inv.petLine}` : ''}
						</Box>
					</Box>
				</Box>

				<Box sx={{ flex: 1, p: '14px 16px 22px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
					{inv.isVoid && <VoidBanner inv={inv} />}
					{justPaid && inv.isPaid && <JustPaidBanner inv={inv} />}

					{/* hero */}
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px', p: '16px 18px 15px', ...card, borderRadius: '16px' }}>
						<Box sx={{ ...overline, color: inv.isPaid ? T.okIcon : T.muted }}>{hero.label}</Box>
						<Box sx={{
							fontSize: '34px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.15, fontVariantNumeric: 'tabular-nums',
							color: inv.isVoid ? '#A9A3BE' : T.text, textDecoration: inv.isVoid ? 'line-through' : 'none',
						}}>
							{hero.amount}
						</Box>
						<Box sx={{ fontSize: '12.5px', fontWeight: 600, color: T.muted, lineHeight: 1.45 }}>{hero.caption}</Box>
					</Box>

					{/* appointment · opens the same screen as the footer button; plain when the appointment was deleted */}
					<Box
						component={canOpenAppointment ? ButtonBase : 'div'}
						onClick={canOpenAppointment ? () => onOpenAppointment(inv) : undefined}
						sx={{
							display: 'flex', alignItems: 'center', gap: '11px', p: '12px 14px', ...card, width: '100%', textAlign: 'left',
							fontFamily: T.font, color: T.text, ...(canOpenAppointment ? { '&:active': { bgcolor: T.bg } } : {}),
						}}
					>
						<Box sx={{ width: 36, height: 36, flex: 'none', borderRadius: '11px', bgcolor: T.primarySoft, color: T.primaryDeep, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
							<InvIcon name="calendar" size={17} sw={1.6} />
						</Box>
						<Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '1px' }}>
							<Box component="strong" sx={{ fontSize: '13.5px', fontWeight: 750, color: T.text }}>{inv.dateFmt} · {inv.time}</Box>
							<Box sx={{ fontSize: '12px', fontWeight: 600, color: T.muted }}>{[inv.groomerLine, inv.petCountLabel].filter(Boolean).join(' · ')}</Box>
						</Box>
						{canOpenAppointment && (
							<Box sx={{ color: T.faint, display: 'flex', flex: 'none' }}><InvIcon name="chevRight" size={16} sw={1.8} /></Box>
						)}
					</Box>

					{/* items + totals */}
					<Box sx={{ ...card }}>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', p: '13px 15px 11px', borderBottom: '1px solid #EFEBF7' }}>
							<Box sx={{ flex: 1, fontSize: '14px', fontWeight: 750, letterSpacing: '-0.01em', color: T.text }}>Items</Box>
							<Box sx={{ fontSize: '11.5px', fontWeight: 650, color: T.muted }}>{inv.petCountLabel}</Box>
						</Box>
						<Box sx={{ p: '12px 15px 15px', display: 'flex', flexDirection: 'column' }}>
							<ItemsByPet inv={inv} petAvatars={petAvatars} />
							<TotalsStack inv={inv} />
						</Box>
					</Box>

					{showPayments && <PaymentsCard inv={inv} />}

					{/* quiet actions */}
					{inv.isUnpaid && onDownload && (
						<Box sx={{ ...card, overflow: 'hidden' }}>
							<ButtonBase disabled={downloading} onClick={() => onDownload(inv)} sx={{
								display: 'flex', alignItems: 'center', gap: '11px', width: '100%', textAlign: 'left',
								p: '13px 15px', fontSize: '13.5px', fontWeight: 650, color: T.text, fontFamily: T.font,
								justifyContent: 'flex-start', opacity: downloading ? 0.6 : 1,
								'&:active': { bgcolor: T.surface },
							}}>
								<Box sx={{ color: T.muted, display: 'flex' }}><InvIcon name="download" size={16} /></Box>
								<Box sx={{ flex: 1 }}>{downloading ? 'Preparing PDF…' : 'Download PDF'}</Box>
								<Box sx={{ color: T.faint, display: 'flex' }}><InvIcon name="chevRight" size={15} sw={1.8} /></Box>
							</ButtonBase>
						</Box>
					)}

					<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', px: '4px', fontSize: '12px', fontWeight: 600, color: T.muted }}>
						<InvIcon name="lock" size={14} sw={1.6} />
						<span>{inv.createdLine}. Items come from the appointment and can't be edited here.</span>
					</Box>
				</Box>

				{/* footer actions */}
				<Box sx={{ flex: 'none', display: 'flex', gap: '9px', p: '12px 16px 14px', bgcolor: T.field, borderTop: `1px solid ${T.border}`, position: 'sticky', bottom: 0 }}>
					{inv.canCheckout && canOpenAppointment
						? <OpenAppointmentButton inv={inv} onOpenAppointment={onOpenAppointment} height={48} />
						: onDownload && (
							<DownloadButton
								inv={inv} onDownload={onDownload} downloading={downloading} height={48} full
								label={inv.isVoid ? 'Download PDF (marked void)' : 'Download PDF'}
							/>
						)}
				</Box>
			</Box>
		)
	}

	/* desktop */
	const bizContact = [inv.biz.phone, inv.biz.email].filter(Boolean).join(' · ')
	const bizMeta = Boolean(inv.biz.address || bizContact) // without it, the name sits centred on the logo
	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', fontFamily: T.font }}>
			{/* header */}
			<Box sx={{ display: 'flex', alignItems: 'center', gap: '13px' }}>
				<ButtonBase aria-label={backLabel} title={backLabel} onClick={onBack} sx={{ width: 38, height: 38, flex: 'none', border: `1.5px solid ${T.border}`, borderRadius: '10px', bgcolor: T.field, color: T.text, '&:hover': { borderColor: T.primary, color: T.primaryDeep } }}>
					<InvIcon name="back" size={16} sw={1.8} />
				</ButtonBase>
				<Box sx={{ flex: 1, minWidth: 0 }}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
						<Box sx={{ fontSize: '20px', fontWeight: 800, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums', color: T.text }}>{inv.number}</Box>
						<StatusPill status={inv.status} />
					</Box>
					<Box sx={{ mt: '2px', fontSize: '12.5px', fontWeight: 600, color: T.muted }}>
						{[inv.clientName, inv.dateLong, inv.groomerLine, inv.petLine].filter(Boolean).join(' · ')}
					</Box>
				</Box>
				{inv.isVoid && canOpenAppointment && (
					<ButtonBase
						title="Opens the cancelled appointment, read-only"
						onClick={() => onOpenAppointment(inv)}
						sx={{
							height: 38, px: '13px', gap: '7px', border: `1.5px solid ${T.border}`, borderRadius: '10px',
							bgcolor: T.field, color: T.text, fontSize: '13px', fontWeight: 650, fontFamily: T.font, whiteSpace: 'nowrap',
							'&:hover': { borderColor: T.primary, color: T.primaryDeep },
						}}
					>
						<InvIcon name="calendar" size={15} sw={1.6} />View appointment
					</ButtonBase>
				)}
				{!inv.isUpcoming && onDownload && (
					<DownloadButton inv={inv} onDownload={onDownload} downloading={downloading} />
				)}
			</Box>

			<Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 340px', gap: '16px', alignItems: 'start' }}>
				{/* the document */}
				<Box sx={{ position: 'relative', ...card, p: '26px 30px 22px', minWidth: 0, overflow: 'hidden' }}>
					{inv.isVoid && (
						<Box sx={{ position: 'absolute', top: 22, right: 26, transform: 'rotate(-10deg)', display: 'inline-flex', alignItems: 'center', height: 32, px: '12px', border: `2.5px solid ${T.danger}`, borderRadius: '7px', color: T.danger, fontSize: '15px', fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase', opacity: 0.85 }}>Void</Box>
					)}
					<Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '20px' }}>
						<Box sx={{ display: 'flex', alignItems: bizMeta ? 'flex-start' : 'center', gap: '12px', minWidth: 0 }}>
							<BrandLogo url={logoUrl} name={inv.biz.name} />
							<Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
								<Box component="strong" sx={{ fontSize: '16px', fontWeight: 800, letterSpacing: '-0.01em', color: T.text }}>{inv.biz.name}</Box>
								{bizMeta && <Box sx={{ fontSize: '11.5px', fontWeight: 600, color: T.muted, lineHeight: 1.5 }}>
									{inv.biz.address && <>{inv.biz.address}<br /></>}
									{bizContact}
								</Box>}
							</Box>
						</Box>
						{!inv.isVoid && (
							<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '3px', textAlign: 'right', flex: 'none' }}>
								<Box sx={overline}>{inv.isPaid ? 'Receipt' : 'Invoice'}</Box>
								<Box component="strong" sx={{ fontSize: '15px', fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: T.text }}>{inv.number}</Box>
								<Box sx={{ fontSize: '11.5px', fontWeight: 600, color: T.muted }}>{inv.docDateLong}</Box>
							</Box>
						)}
					</Box>
					<Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', m: '18px 0 14px', py: '13px', borderTop: `1px solid ${T.divider}`, borderBottom: `1px solid ${T.divider}` }}>
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
							<Box sx={overline}>Billed to</Box>
							<Box component="strong" sx={{ fontSize: '13.5px', fontWeight: 750, mt: '2px', color: T.text }}>{inv.clientName}</Box>
							<Box sx={{ fontSize: '12px', fontWeight: 600, color: T.muted, fontVariantNumeric: 'tabular-nums' }}>{[inv.clientPhone, inv.clientEmail].filter(Boolean).join(' · ')}</Box>
						</Box>
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
							<Box sx={overline}>Appointment</Box>
							<Box component="strong" sx={{ fontSize: '13.5px', fontWeight: 750, mt: '2px', color: T.text }}>{inv.dateLong} · {inv.time}</Box>
							<Box sx={{ fontSize: '12px', fontWeight: 600, color: T.muted }}>{[inv.groomerLine, inv.petCountLabel].filter(Boolean).join(' · ')}</Box>
						</Box>
					</Box>
					<ItemsByPet inv={inv} petAvatars={petAvatars} />
					<Box sx={{ ml: 'auto', width: 340, maxWidth: '100%' }}>
						<TotalsStack inv={inv} />
					</Box>
				</Box>

				{/* rail */}
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', minWidth: 0 }}>
					{inv.isVoid && <VoidBanner inv={inv} />}
					<Box sx={{ ...card, p: '16px 17px 17px', display: 'flex', flexDirection: 'column' }}>
						<Box sx={{ ...overline, color: inv.isPaid ? T.okIcon : T.muted }}>{hero.label}</Box>
						<Box sx={{
							mt: '4px', fontSize: '28px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.15, fontVariantNumeric: 'tabular-nums',
							color: inv.isVoid ? '#A9A3BE' : T.text, textDecoration: inv.isVoid ? 'line-through' : 'none',
						}}>
							{hero.amount}
						</Box>
						<Box sx={{ mt: '4px', fontSize: '12px', fontWeight: 600, color: T.muted, lineHeight: 1.5 }}>{hero.caption}</Box>
						{inv.canCheckout && canOpenAppointment && (
							<Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', mt: '14px' }}>
								<OpenAppointmentButton inv={inv} onOpenAppointment={onOpenAppointment} />
							</Box>
						)}
						{justPaid && inv.isPaid && (
							<Box sx={{ mt: '14px' }}><JustPaidBanner inv={inv} /></Box>
						)}
						{inv.isPaid && onDownload && (
							<Box sx={{ mt: '14px' }}>
								<DownloadButton inv={inv} onDownload={onDownload} downloading={downloading} height={40} full />
							</Box>
						)}
					</Box>
					{showPayments && <PaymentsCard inv={inv} dense />}
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', p: '2px 4px', fontSize: '12px', fontWeight: 600, color: T.muted }}>
						<InvIcon name="lock" size={14} sw={1.6} />
						<span>{inv.createdLine}. Items come from the appointment and can't be edited here.</span>
					</Box>
				</Box>
			</Box>
		</Box>
	)
}
